import { useEffect, useCallback, useRef } from 'react';
import { useCharacterStore } from './useCharacterStore';
import { useStreakSystem } from './useStreakSystem';
import { useModuleMoneyAward } from './useModuleMoneyAward';
import { log, logError } from '@/lib/log';

interface QuestionPointsEvent {
  points: number;
  questionId: string;
  moduleId: string;
  sectionId?: string;
  isCorrect: boolean;
}

// Global processed set to prevent duplicate event processing
// This persists across component mounts and is shared by all instances
const globalProcessedQuestions = new Set<string>();

/**
 * Design decision: 1 point = 1 € in the student's money.
 * This keeps the mental model simple: question points and money are aligned.
 * 
 * Uses the idempotent award_module_question_money RPC which:
 * - Records awards in player_module_money_awards (prevents farming)
 * - Updates player_financial_status atomically
 * - Returns the new total earned
 */
export const useQuestionPointsIntegration = () => {
  const { addPoints } = useCharacterStore();
  const { recordCorrectAnswer, recordIncorrectAnswer } = useStreakSystem();
  const { awardQuestionMoney } = useModuleMoneyAward();
  
  // Use ref to avoid stale closures
  const awardQuestionMoneyRef = useRef(awardQuestionMoney);
  
  useEffect(() => {
    awardQuestionMoneyRef.current = awardQuestionMoney;
  }, [awardQuestionMoney]);

  const handleQuestionPoints = useCallback(async (event: QuestionPointsEvent) => {
    // Create a unique key for this question to prevent double-processing
    const processKey = `${event.moduleId}:${event.questionId}`;
    
    // Check if already processed (guards against duplicate event listeners)
    if (globalProcessedQuestions.has(processKey)) {
      log('[points] Skipping duplicate event for question:', processKey);
      return;
    }
    
    // Mark as processed immediately
    globalProcessedQuestions.add(processKey);
    
    if (event.isCorrect) {
      // Record correct answer for streak system (this handles streak bonus points, NOT base money)
      recordCorrectAnswer(event.questionId, event.moduleId, event.points);
      
      // Add base points for correct answer (points determine level progression)
      addPoints(event.points);

      // Design decision: 1 point = 1 € - Direct conversion from question points to money
      const moneyAmount = event.points;
      
      log('[points] Converting points to money', {
        points: event.points,
        moneyAmount,
        questionId: event.questionId,
        moduleId: event.moduleId
      });

      // Award money via idempotent RPC (prevents farming same question)
      if (moneyAmount > 0) {
        try {
          const result = await awardQuestionMoneyRef.current(
            event.questionId,
            event.sectionId || 'quiz-section',
            event.moduleId,
            moneyAmount
          );
          
          if (result.awarded) {
            log(`[points] Money awarded: ${result.eurosDelta}€, new total: ${result.newTotalEarned}€`);
          } else if (result.reason === 'already_awarded') {
            log('[points] Question already awarded (idempotent), skipping');
          } else if (!result.success) {
            logError('[points] Failed to award money (result):', result);
          }
        } catch (err) {
          // Log full error object for debugging
          console.error('[points] Exception awarding money (raw):', err);
          const msg = err instanceof Error ? err.message : (typeof err === 'string' ? err : JSON.stringify(err));
          logError('[points] Exception awarding money (msg):', msg);
        }
      }

      log(`[points] Question complete: +${event.points} points from ${event.questionId}`);
    } else {
      // Record incorrect answer (breaks streak)
      recordIncorrectAnswer();
    }
  }, [addPoints, recordCorrectAnswer, recordIncorrectAnswer]);

  // Listen for question completion events
  useEffect(() => {
    const handleQuestionCompleted = (event: CustomEvent<QuestionPointsEvent>) => {
      handleQuestionPoints(event.detail);
    };

    window.addEventListener('questionCompleted', handleQuestionCompleted as EventListener);

    return () => {
      window.removeEventListener('questionCompleted', handleQuestionCompleted as EventListener);
    };
  }, [handleQuestionPoints]);

  // Clear processed set on unmount (optional - keeps memory clean for long sessions)
  useEffect(() => {
    return () => {
      // Don't clear on individual component unmount - only clear if all instances are gone
      // This is handled by the global singleton pattern
    };
  }, []);

  return { handleQuestionPoints };
};
